// Página AdminUsers - Gestión de usuarios del sistema
// Permite ver, crear, editar y gestionar usuarios y sus roles

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getAllUsers, updateUserRole, deleteUser } from '../../services/users.service'
import { getAllRoles } from '../../services/roles.service'
import { formatDate } from '../../utils/helpers'
import { FIXED_ROLES } from '../../constants/permissions'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import './AdminUsers.css'

/**
 * Página AdminUsers
 * Gestión de usuarios del sistema para administradores
 */
function AdminUsers() {
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [customRoles, setCustomRoles] = useState([])

  // Filtros y búsqueda
  const [roleFilter, setRoleFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortDirection, setSortDirection] = useState('desc')

  // Estados para modales
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [notification, setNotification] = useState(null)

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    admins: 0,
    users: 0
  })

  const fetchCustomRoles = useCallback(async () => {
    try {
      const roles = await getAllRoles()
      setCustomRoles(roles.filter(r => r.active))
    } catch (err) {
      console.error('Error cargando roles personalizados:', err)
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const usersData = await getAllUsers()
      setUsers(usersData)
      
      // Calcular estadísticas
      calculateStats(usersData)
    } catch (err) {
      console.error('Error cargando usuarios:', err)
      setError('Error al cargar los usuarios')
    } finally {
      setLoading(false)
    }
  }, [])

  const calculateStats = (usersData) => {
    const total = usersData.length
    const admins = usersData.filter(u => 
      u.role === FIXED_ROLES.ADMIN || u.role === FIXED_ROLES.SUPERADMIN
    ).length
    const clients = usersData.filter(u => 
      u.role === FIXED_ROLES.CLIENT && !u.roleId
    ).length

    setStats({ total, admins, users: clients })
  }

  const applyFilters = useCallback(() => {
    let filtered = [...users]

    // Filtrar por rol
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        // Si el filtro es un ID de rol personalizado, verificar roleId
        if (customRoles.some(r => r.id === roleFilter)) {
          return user.roleId === roleFilter
        }
        // Si no, verificar el rol fijo
        return user.role === roleFilter
      })
    }

    // Filtrar por búsqueda (nombre, email)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(user => {
        const name = (user.displayName || '').toLowerCase()
        const email = (user.email || '').toLowerCase()
        return name.includes(search) || email.includes(search)
      })
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[sortBy]
      let bValue = b[sortBy]

      // Manejar fechas
      if (sortBy === 'createdAt') {
        aValue = aValue ? new Date(aValue).getTime() : 0
        bValue = bValue ? new Date(bValue).getTime() : 0
      }

      // Manejar strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase()
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

    setFilteredUsers(filtered)
  }, [users, roleFilter, searchTerm, sortBy, sortDirection, customRoles])

  // Cargar usuarios y roles
  useEffect(() => {
    fetchUsers()
    fetchCustomRoles()
  }, [fetchUsers, fetchCustomRoles])

  // Aplicar filtros y ordenamiento
  useEffect(() => {
    applyFilters()
  }, [applyFilters])
  const handleRoleChange = async (user, newRole, roleId = null) => {
    try {
      await updateUserRole(user.id, newRole, roleId)
      
      const roleName = roleId 
        ? customRoles.find(r => r.id === roleId)?.name || newRole
        : newRole
      
      setNotification({
        type: 'success',
        message: `Rol de ${user.email} actualizado a ${roleName}`
      })
      setTimeout(() => setNotification(null), 3000)

      // Actualizar en el estado local
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, role: newRole, roleId: roleId || null } : u
        )
      )
    } catch (err) {
      console.error('Error actualizando rol:', err)
      setNotification({
        type: 'error',
        message: 'Error al actualizar el rol del usuario'
      })
      setTimeout(() => setNotification(null), 3000)
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      await deleteUser(selectedUser.id)
      
      setNotification({
        type: 'success',
        message: `Usuario ${selectedUser.email} eliminado correctamente`
      })
      setTimeout(() => setNotification(null), 3000)

      // Recargar usuarios
      await fetchUsers()
    } catch (err) {
      console.error('Error eliminando usuario:', err)
      setNotification({
        type: 'error',
        message: 'Error al eliminar el usuario'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setShowDeleteDialog(false)
      setSelectedUser(null)
    }
  }

  if (loading) {
    return (
      <div className="admin-users-page">
        <div className="loading-state">
          <p>Cargando usuarios...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-users-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchUsers}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-users-page">
      {/* Toast de notificación */}
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="users-header">
        <div>
          <h1>Gestión de Usuarios</h1>
          <p className="users-subtitle">
            Administra usuarios del sistema y sus roles
          </p>
        </div>
        <Link to="/admin/usuarios/nuevo">
          <Button variant="primary">
            ➕ Crear Usuario
          </Button>
        </Link>
      </div>

      {/* Estadísticas */}
      <div className="users-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total de Usuarios</div>
        </div>
        <div className="stat-card stat-admin">
          <div className="stat-value">{stats.admins}</div>
          <div className="stat-label">Administradores</div>
        </div>
        <div className="stat-card stat-user">
          <div className="stat-value">{stats.users}</div>
          <div className="stat-label">Clientes</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="users-filters">
        <div className="filter-group">
          <label htmlFor="roleFilter">Rol:</label>
          <select
            id="roleFilter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value={FIXED_ROLES.CLIENT}>Cliente</option>
            <option value={FIXED_ROLES.ADMIN}>Administrador</option>
            <option value={FIXED_ROLES.SUPERADMIN}>Super Admin</option>
            {customRoles.length > 0 && (
              <optgroup label="Roles Personalizados">
                {customRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Buscar:</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Nombre, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="sortBy">Ordenar por:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="createdAt">Fecha de Registro</option>
            <option value="displayName">Nombre</option>
            <option value="email">Email</option>
            <option value="role">Rol</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sortDirection">Dirección:</label>
          <select
            id="sortDirection"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
          >
            <option value="desc">Descendente</option>
            <option value="asc">Ascendente</option>
          </select>
        </div>
      </div>

      {/* Tabla de usuarios - Desktop */}
      <div className="users-table-container">
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron usuarios con los filtros aplicados</p>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Fecha de Registro</th>
                <th>Email Verificado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const createdDate = user.createdAt
                  ? formatDate(new Date(user.createdAt))
                  : 'N/A'

                return (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar-small">
                          {user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <span className="user-name">{user.displayName || 'Sin nombre'}</span>
                      </div>
                    </td>
                    <td className="email-cell">{user.email}</td>
                    <td>
                      <select
                        className="role-select"
                        value={user.roleId || user.role || FIXED_ROLES.CLIENT}
                        onChange={(e) => {
                          const value = e.target.value
                          // Si es un rol fijo
                          if ([FIXED_ROLES.SUPERADMIN, FIXED_ROLES.ADMIN, FIXED_ROLES.CLIENT].includes(value)) {
                            handleRoleChange(user, value, null)
                          } else {
                            // Es un rol personalizado
                            const customRole = customRoles.find(r => r.id === value)
                            if (customRole) {
                              handleRoleChange(user, customRole.name, value)
                            }
                          }
                        }}
                      >
                        <option value={FIXED_ROLES.CLIENT}>Cliente</option>
                        <option value={FIXED_ROLES.ADMIN}>Administrador</option>
                        <option value={FIXED_ROLES.SUPERADMIN}>Super Admin</option>
                        {customRoles.length > 0 && (
                          <optgroup label="Roles Personalizados">
                            {customRoles.map(role => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </td>
                    <td>{createdDate}</td>
                    <td>
                      <span className={user.emailVerified ? 'verified-badge' : 'unverified-badge'}>
                        {user.emailVerified ? '✓ Verificado' : '✗ No verificado'}
                      </span>
                    </td>
                    <td>
                      <div className="user-actions">
                        <Link to={`/admin/usuarios/${user.id}`}>
                          <button
                            className="edit-btn"
                            title="Editar usuario"
                          >
                            ✏️
                          </button>
                        </Link>
                        <button
                          className="delete-btn"
                          onClick={() => {
                            setSelectedUser(user)
                            setShowDeleteDialog(true)
                          }}
                          title="Eliminar usuario"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Cards de usuarios - Mobile */}
      <div className="users-cards-container">
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron usuarios con los filtros aplicados</p>
          </div>
        ) : (
          filteredUsers.map((user) => {
            const createdDate = user.createdAt
              ? formatDate(new Date(user.createdAt))
              : 'N/A'

            return (
              <div key={user.id} className="user-card">
                <div className="user-card-header">
                  <div className="user-card-avatar">
                    <div className="user-avatar-small">
                      {user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <h3>{user.displayName || 'Sin nombre'}</h3>
                      <p className="user-card-email">{user.email}</p>
                    </div>
                  </div>
                  <span className={user.emailVerified ? 'verified-badge' : 'unverified-badge'}>
                    {user.emailVerified ? '✓ Verificado' : '✗ No verificado'}
                  </span>
                </div>
                <div className="user-card-body">
                  <div className="user-card-row">
                    <span className="info-label">Rol:</span>
                    <select
                      className="role-select"
                      value={user.roleId || user.role || FIXED_ROLES.CLIENT}
                      onChange={(e) => {
                        const value = e.target.value
                        // Si es un rol fijo
                        if ([FIXED_ROLES.SUPERADMIN, FIXED_ROLES.ADMIN, FIXED_ROLES.CLIENT].includes(value)) {
                          handleRoleChange(user, value, null)
                        } else {
                          // Es un rol personalizado
                          const customRole = customRoles.find(r => r.id === value)
                          if (customRole) {
                            handleRoleChange(user, customRole.name, value)
                          }
                        }
                      }}
                    >
                      <option value={FIXED_ROLES.CLIENT}>Cliente</option>
                      <option value={FIXED_ROLES.ADMIN}>Administrador</option>
                      <option value={FIXED_ROLES.SUPERADMIN}>Super Admin</option>
                      {customRoles.length > 0 && (
                        <optgroup label="Roles Personalizados">
                          {customRoles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  <div className="user-card-row">
                    <span className="info-label">Fecha de Registro:</span>
                    <span className="info-value">{createdDate}</span>
                  </div>
                </div>
                <div className="user-card-actions">
                  <Link to={`/admin/usuarios/${user.id}`}>
                    <Button variant="outline" size="small" style={{ width: '100%' }}>
                      ✏️ Editar
                    </Button>
                  </Link>
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => {
                      setSelectedUser(user)
                      setShowDeleteDialog(true)
                    }}
                    style={{ width: '100%' }}
                  >
                    🗑️ Eliminar
                  </Button>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Diálogo de confirmación de eliminación */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Eliminar Usuario"
        message={`¿Estás seguro de que deseas eliminar al usuario ${selectedUser?.email}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={handleDeleteUser}
        onClose={() => {
          setShowDeleteDialog(false)
          setSelectedUser(null)
        }}
      />
    </div>
  )
}

export default AdminUsers

